import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type SectionRow } from '../../api.ts';
import { ErrorNotice, Loading, formatDate } from '../../components/Bits.tsx';
import { AdminChrome, useAdminSession } from './AdminChrome.tsx';

interface ApplicantDetail {
  applicant: Record<string, string | number | boolean | null>;
  sections: Record<string, SectionRow[]>;
  essays: Array<{ question: string; answer: string | null }>;
  financial: { income: string | null; siblings: string | null } | null;
}

const SECTION_TITLES: Array<[string, string]> = [
  ['schools', 'Education'],
  ['awards', 'Awards and honors'],
  ['community', 'Community service'],
  ['extracurricular', 'Extracurricular activities'],
  ['jobs', 'Work experience'],
  ['recommendations', 'References'],
];

/** Legacy integer columns store 0 where the field was left blank. */
const present = (value: unknown) =>
  value !== null && value !== undefined && value !== '' && value !== 0;

function Rows({ rows }: { rows: SectionRow[] }) {
  if (!rows.length) return <p className="empty">None recorded.</p>;
  return (
    <>
      {rows.map((row) => (
        <div className="entry" key={row.id}>
          <dl className="dl">
            {Object.entries(row)
              .filter(([key, value]) =>
                !['id', 'applicant_id', 'created_at', 'updated_at'].includes(key) && present(value))
              .map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt>{key.replace(/_/g, ' ')}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
          </dl>
        </div>
      ))}
    </>
  );
}

export function AdminApplicant() {
  const { id } = useParams();
  const { data: session, isPending: sessionPending } = useAdminSession();

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'applicant', id],
    queryFn: () => api.get<ApplicantDetail>(`/api/admin/applicants/${id}`),
    enabled: Boolean(session?.admin && id),
  });

  if (sessionPending) return <main id="main"><Loading /></main>;
  if (!session?.admin) return <Navigate to="/admin/signin" replace />;

  return (
    <>
      <AdminChrome admin={session.admin} />
      <main id="main">
        <p><Link to="/admin">← Back to applications</Link></p>

        {isPending && <Loading />}
        <ErrorNotice error={error} />

        {data && (
          <>
            <h2>
              {[data.applicant.first_name, data.applicant.middle_name, data.applicant.last_name]
                .filter(Boolean).join(' ') || `Applicant ${data.applicant.id}`}
            </h2>
            <p className="lede">
              {data.applicant.cycle_year} ·{' '}
              {data.applicant.completed
                ? `submitted ${formatDate(data.applicant.submitted_at as string)}`
                : 'not submitted'}
              {' · '}
              <a href={`/api/admin/applicants/${id}/pdf`}>Download PDF</a>
            </p>

            <div className="panel">
              <dl className="dl">
                <dt>Email</dt><dd>{String(data.applicant.email)}</dd>
                <dt>Telephone</dt><dd>{String(data.applicant.telephone ?? '')}</dd>
                <dt>Address</dt>
                <dd>
                  {[data.applicant.address, data.applicant.city, data.applicant.state, data.applicant.zip]
                    .filter(Boolean).join(', ')}
                </dd>
                <dt>Date of birth</dt><dd>{String(data.applicant.dob ?? '')}</dd>
                <dt>Verification form</dt>
                <dd>{data.applicant.verification_form ? 'Returned' : 'Not returned'}</dd>
                {data.financial && (
                  <>
                    <dt>Household income</dt><dd>{data.financial.income ?? '—'}</dd>
                    <dt>Siblings in college</dt><dd>{data.financial.siblings ?? '—'}</dd>
                  </>
                )}
              </dl>
            </div>

            <h3>Essays</h3>
            {data.essays.length === 0 && <p className="empty">None recorded.</p>}
            {data.essays.map((essay, index) => (
              <div className="panel" key={index}>
                <p style={{ marginTop: 0, fontWeight: 600 }}>{essay.question}</p>
                {/* Preserve the applicant's paragraph breaks rather than running it together. */}
                <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {essay.answer?.trim() || <span className="empty">No answer.</span>}
                </p>
              </div>
            ))}

            {SECTION_TITLES.map(([key, title]) => (
              <div key={key}>
                <h3>{title}</h3>
                <Rows rows={data.sections[key] ?? []} />
              </div>
            ))}
          </>
        )}
      </main>
    </>
  );
}
